import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useLeaveData } from "../../../hooks/leaveData";
import axios from "../../../api/axios";
import { destroySession } from "../../../helpers/Session";
import { useNavigate } from "react-router-dom";
import toast from "../../sweetalert/Sweetalert";

const LeaveCredits = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { leaveData } = useLeaveData();

    const timeoutRef = useRef(null);

    const [formSubmit, setFormSubmit] = useState(false);
    const [leaveCreditsValue, setLeaveCreditsValue] = useState({});

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const uname = sessionStorage.getItem('uname');
    const department = sessionStorage.getItem('department');
    const departmentslug = sessionStorage.getItem('departmentslug');

    const userLeaveCreditsDataKey = ['userLeaveCredits', leaveData?.year, leaveData?.employee];

    const { data: leavecredits, isLoading: isLoadingLeaveCredits, error: errorLeaveCredits } = useQuery({
        queryKey: userLeaveCreditsDataKey,
        queryFn: async () => {
            const res = await axios.get(`leaves/employees?uname=${leaveData?.employee}&year=${leaveData?.year}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!leaveData?.employee && !!leaveData?.year && !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorLeaveCredits) {
            if (errorLeaveCredits.response?.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error') }, 1000);
            }
        }
    }, errorLeaveCredits);

    useEffect(() => {
        if (leavecredits) {
            const initialCredits = {};
            leavecredits?.forEach(item => {
                initialCredits[item.id] = item.quantity || 0;
            });
            setLeaveCreditsValue(initialCredits);
        }
    }, [leavecredits]);

    const handleCreditValueChange = (id, value) => {
        setLeaveCreditsValue((prev) => ({
            ...prev,
            [id]: value,
        }))
    }

    const employeeLeaveCreditMutation = useMutation({
        mutationFn: async (employeeData) => {
            const res = await axios.post('leaves/employees', employeeData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = (error.response.data?.response) ? error.response.data?.response : 'Something went wrong while saving employee leave credits!';
            toast.fire({ icon: 'error', title: errorMessage });

            if (error?.response.data?.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        },
        onSuccess: async (response) => {
            setFormSubmit(false);
            toast.fire({ icon: 'success', title: response });
            queryClient.invalidateQueries(userLeaveCreditsDataKey);
        }
    });

    const handleSubmitEmployeeLeaveCredits = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        formData.append('year', leaveData?.year);
        formData.append('uname', leaveData?.employee);
        formData.append('creator', uname);
        formData.append('department', department);
        formData.append('departmentcode', departmentslug);

        setFormSubmit(true);

        employeeLeaveCreditMutation.mutate(formData);
    }

    return (
        <div className="credits-input">
            <form onSubmit={handleSubmitEmployeeLeaveCredits}>
                <div className="panel">
                    <div className="panel-title">
                        <h3>Credits</h3>
                        <button type="submit" disabled={formSubmit}>
                            {
                                formSubmit
                                    ? (<span>SAVING <i className="fa fa-spinner fa-spin"></i></span>)
                                    : ('SAVE')
                            }
                        </button>
                    </div>
                    <div className="panel-content">
                        <table className={isLoadingLeaveCredits ? 'loading' : ''}>
                            <thead>
                                <tr>
                                    <th>Leave</th>
                                    <th>Quantity</th>
                                    <th>Used Leave</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingLeaveCredits
                                    ? (
                                        [...Array(10)].map((_, i) => (
                                            <tr key={i}>
                                                <td><span></span></td>
                                                <td><span></span></td>
                                                <td><span></span></td>
                                            </tr>
                                        ))
                                    ) : (
                                        leavecredits?.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <span>{item.leave}</span>
                                                    {!item.saved && (<i className="fa fa-exclamation-circle not-saved" title="Not Yet Saved!"></i>)}
                                                </td>
                                                <td>
                                                    <span>
                                                        <input type="hidden" name="leave[]" value={item.id} />
                                                        <input type="number" name="quantity[]" value={leaveCreditsValue[item.id] || 0} min="0.5" step="0.5" onInput={(e) => { handleCreditValueChange(item.id, e.target.value) }} />
                                                    </span>
                                                </td>
                                                <td>
                                                    <span>{item.usedleave}</span>
                                                </td>
                                            </tr>
                                        ))
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default LeaveCredits;